import { Component, computed, signal, WritableSignal, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BoFormsModule, FormComponent } from '@bo/ng-forms';
import { Method } from '@bo/ng-api';
import { FieldComponent } from '../../../../../shared/components/forms/form-field/form-field.component';
import { SubmitComponent } from '../../../../../shared/components/forms/submit/submit.component';
import { SelectFieldComponent } from '../../../../../shared/components/forms/select-field/select-field.component';
import { SubscriptionCardRequest } from '../../../../../_interfaces/cards.interface';
import { OffcanvasService } from '../../../../../_services/offcanvas-service/offcanvas.service';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { CardsApiService } from '../../../../../_services/cards-service/cards-api/cards-api.service';
import { ToastService } from '../../../../../_services/toast-service/toast.service';
import { HttpErrorResponse } from '@angular/common/http';
import { LoaderComponent } from '../../../../../shared/components/loader/loader.component';
import { OffcanvasBaseComponent } from '../../../../../shared/components/offcanvas/offcanvas-base/offcanvas-base.component';
import { Store } from '@ngxs/store';
import { SetAppStateFromSubscriptionCardCreated } from '../../../../../store/app/app.actions';
import { EmployeeService } from '../../../../../_services/employee-service/employee.service';
import { maskConfig } from '../../../../../shared/configs/configs';
import { ToggleSwitchComponent } from '../../../../../shared/components/forms/toggle-switch/toggle-switch.component';
import { TopUpFrequency } from '../../../../../_interfaces/employee.interface';
import { SelectTopupFrequencyComponent } from '../../../../../shared/components/forms/select-topup-frequency/select-topup-frequency.component';
import { CardLimitDisplayComponent } from '../../../../../shared/components/cards/card-limit-display/card-limit-display.component';
import { helpCenterUrl, topupTooltipText } from '../../../../../_fixtures/project-fixtures';
import { catchError } from 'rxjs';
import { filterLettersOnly } from '../../../../../shared/utils/form-helpers';
import { toSignal } from '@angular/core/rxjs-interop';
import { DashboardService } from '../../../../../_services/dashboard-api-service/dashboard.service';
import { NoCardsAvailableDisplayComponent } from '../../../../../shared/components/cards/no-cards-available-display/no-cards-available-display.component';

@Component({
  selector: 'app-create',
  imports: [
    ReactiveFormsModule,
    BoFormsModule,
    FieldComponent,
    SubmitComponent,
    SelectFieldComponent,
    LoaderComponent,
    OffcanvasBaseComponent,
    ToggleSwitchComponent,
    SelectTopupFrequencyComponent,
    CardLimitDisplayComponent,
    NoCardsAvailableDisplayComponent,
  ],
  providers: [NgbActiveOffcanvas],
  templateUrl: './create-subscription-card.component.html',
})
export class CreateSubscriptionCardComponent extends FormComponent<SubscriptionCardRequest> {
  readonly offcanvasService = inject(OffcanvasService);
  readonly employeeService = inject(EmployeeService);
  readonly cardsApiService = inject(CardsApiService);
  readonly toastService = inject(ToastService);
  readonly dashboardService = inject(DashboardService);
  readonly store = inject(Store);

  employeeAdminOptions$ = this.employeeService.employeeTypeAdminOptions();
  availableCards = toSignal(this.dashboardService.getAvailableCardCount(), { initialValue: null });
  checkingCards = computed(() => this.dashboardService.isCheckingCardAvailability());
  hasAvailableCards = computed(() => (this.availableCards() ?? 0) > 0);

  protected readonly helpCenterUrl = helpCenterUrl;
  protected returnUrl: string = 'cards/subscription-cards';
  protected fields: string[] = ['name', 'amount', 'holder'];
  protected readonly maskConfig = maskConfig;
  protected showRecurringTopUp = signal<boolean>(false);
  protected selectedFrequency: WritableSignal<TopUpFrequency> = signal<TopUpFrequency>(TopUpFrequency.Monthly);
  defaultFrequency: TopUpFrequency = TopUpFrequency.Monthly;
  protected hasCardLimitError = signal<boolean>(false);
  protected readonly tooltipText = topupTooltipText;
  protected readonly filterLettersOnly = filterLettersOnly;

  createForm(): void {
    this.subscriptions.add(
      this.buildFromOptions(
        this.cardsApiService.getOptions(this.cardsApiService.URL_CARD_SUBSCRIPTION, Method.POST, true),
      ).subscribe({
        next: () => {
          this.handleTopUpAmountChanges();
          this.setAmountField();
        },
      }),
    );
  }

  setAmountField() {
    if (this.showRecurringTopUp()) {
      this.form.get('amount')?.enable();
    } else {
      this.form.get('amount')?.disable();
    }
  }

  onToggleRecurring(event: boolean) {
    this.showRecurringTopUp.set(event);
    this.setAmountField();
  }

  handleFrequencyChange(value: TopUpFrequency) {
    this.selectedFrequency.set(value);
  }

  handleCardLimitError($event: boolean) {
    this.hasCardLimitError.set($event);
  }

  protected handleTopUpAmountChanges(): void {
    this.form.get('amount')?.valueChanges.subscribe((amount) => {
      if (amount === 0) this.form.get('amount')?.setValue(undefined, { emitEvent: false });
    });
  }

  submitData(formData: SubscriptionCardRequest) {
    formData.topup = this.showRecurringTopUp();
    if (this.showRecurringTopUp()) formData.frequency = this.selectedFrequency();
    return this.cardsApiService.createSubscriptionCard(formData).pipe(
      catchError(() => {
        this.toastService.show('Something went wrong.', 'ic-close');
        return [];
      }),
    );
  }

  handleSubmitSuccess(): void {
    this.offcanvasService.close(this.returnUrl);
    this.store.dispatch(new SetAppStateFromSubscriptionCardCreated());
    this.toastService.show('You have successfully created a subscription card.', 'iconoir-check-circle');
  }

  protected override handleSubmitError(error: unknown) {
    // If the error is related to the card limit being reached
    if (error instanceof HttpErrorResponse && error.error.card) {
      const message = error.error.card[0];
      this.form.setErrors({ server: message });
      return;
    }
    // Else use the default error handling
    super.handleSubmitError(error);
  }
}
